// Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CartIcon, AccountIcon, MenuIcon, CloseIcon, LogoIcon } from '../public/icons';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import ShoppingBagModal from './auth/ShoppingBagModal';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [cartCount, setCartCount] = useState(0); // 🟢 live cart count
  const location = useLocation();
  const mobileMenuRef = useRef(null);

  const { user, isAuthenticated, logout } = useAuth();
  const { openModal } = useModal();

  /* ───────── Read cart count from localStorage ───────── */
  const computeCartCount = () => {
    try {
      const raw = localStorage.getItem('memoryMagnetsCart');
      const cart = raw ? JSON.parse(raw) : [];
      // Count total items (sum of quantities). Use cart.length for unique lines.
      return cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
    } catch {
      return 0;
    }
  };

  /* ───────── Sync cart count on mount + on cart changes ─────────
     Also listens for the global `openShoppingBag` event so that
     "Add to Bag" flows (e.g. MemoryMagnetEdit) can pop the sidebar
     open automatically. */
  useEffect(() => {
    setCartCount(computeCartCount());

    const syncCount = () => setCartCount(computeCartCount());

    const openBag = () => {
      console.log('🟢 openShoppingBag event received — opening cart');
      setShowCartModal(true);
    };

    window.addEventListener('cartUpdated', syncCount);
    window.addEventListener('storage', syncCount);
    window.addEventListener('openShoppingBag', openBag); // ✅ NEW

    return () => {
      window.removeEventListener('cartUpdated', syncCount);
      window.removeEventListener('storage', syncCount);
      window.removeEventListener('openShoppingBag', openBag); // ✅ NEW
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest('button[aria-label="Menu"]')
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  const navLinks = [
    { name: 'Shop', href: '/shop', path: '/shop' },
    { name: 'Kapta Memoir', href: '/memoirs', path: '/memoirs' },
    { name: 'Contact', href: '/contact', path: '/contact' },
  ];

  const orderMenuItems = [
    { name: 'All Orders', path: '/account/orders/all' },
    { name: 'Processing Orders', path: '/account/orders/processing' },
    { name: 'Shipped Orders', path: '/account/orders/shipped' },
    { name: 'Delivered Orders', path: '/account/orders/delivered' },
    { name: 'Cancelled Orders', path: '/account/orders/cancelled' },
    { name: 'Future Gifts', path: '/account/orders/future-gifts', badge: 1 },
  ];

  const profileMenuItems = [
    { name: 'Profile Information', path: '/account/profile' },
    { name: 'Addresses', path: '/account/addresses' },
    { name: 'Notification', path: '/account/notification' },
    { name: 'Security', path: '/account/security' },
  ];

  const isActiveLink = (linkPath) => {
    if (linkPath === '#') return false;

    if (
      linkPath === '/shop' ||
      linkPath === '/memoirs' ||
      linkPath === '/contact'
    ) {
      return location.pathname === linkPath;
    }

    if (linkPath === '/blog') {
      return location.pathname.startsWith('/blog');
    }

    return location.pathname === linkPath;
  };

  const getNavbarColors = () => {
    const isHomePage = location.pathname === '/';
    const isShopPage = location.pathname === '/shop';
    const isMemoirPage = location.pathname === '/memoirs';

    if (isScrolled) {
      return {
        background: '#FCF7EE',
        text: '#1C2A46',
        iconColor: '#1C2A46',
        activeColor: '#D2A146',
        shadow: true
      };
    }

    if  (isMemoirPage) {
      return {
        background: 'transparent',
        text: '#FFFFFF',
        iconColor: '#FFFFFF',
        activeColor: '#D2A146',
        shadow: false
      };
    }

    if (isHomePage) {
      return {
        background: 'transparent',
        text: '#FFFFFF',
        iconColor: '#FFFFFF',
        activeColor: '#D2A146',
        shadow: false
      };
    }

    return {
      background: '#FCF7EE',
      text: '#1C2A46',
      iconColor: '#1C2A46',
      activeColor: '#D2A146',
      shadow: true
    };
  };

  const colors = getNavbarColors();

  const handleAccountClick = () => {
    if (!isAuthenticated) {
      openModal('signin');
    }
    setIsMenuOpen(false);
  };

  const handleCartClick = () => {
    setShowCartModal(true);
    setIsMenuOpen(false);
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getUserInitials = () => {
    if (!user) return 'U';
    return `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
  };

  const NavLink = ({ link, isMobile = false }) => {
    const isActive = isActiveLink(link.path);

    return (
      <div className="relative">
        <Link
          to={link.href}
          className={`relative font-medium transition-all duration-200 ${
            isMobile
              ? 'py-4 px-6 w-full text-left hover:bg-[#F8F8F8] active:bg-[#F0F0F0]'
              : 'text-base hover:opacity-70'
          }`}
          style={{
            color: isMobile ? '#1C2A46' : colors.text
          }}
          onClick={() => isMobile && setIsMenuOpen(false)}
        >
          <div className="flex items-center justify-between">
            <span>{link.name}</span>
            {isMobile && isActive && (
              <div
                className="w-2 h-2 rounded-full ml-2"
                style={{ backgroundColor: colors.activeColor }}
              />
            )}
          </div>
        </Link>

        {isActive && !isMobile && (
          <div
            className="absolute left-1/2 transform -translate-x-1/2 mt-3 w-12 h-[1px] "
            style={{
              backgroundColor: colors.activeColor,
              top: '100%'
            }}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          colors.shadow ? 'shadow-sm' : ''
        }`}
        style={{
          backgroundColor: colors.background,
          paddingTop: isScrolled ? '1rem' : '1.5rem',
          paddingBottom: isScrolled ? '1rem' : '1.5rem'
        }}
      >
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Left Nav Links */}
            <div className="hidden lg:flex items-center space-x-10 flex-1">
              {navLinks.slice(0, 2).map((link) => (
                <div key={link.name} className="relative h-full flex items-center">
                  <NavLink link={link} />
                </div>
              ))}
            </div>

            {/* Center Logo */}
            <div className="flex justify-center lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2">
              <Link to="/" className="flex items-center">
                <LogoIcon
                  className="hover:opacity-80 transition-opacity duration-200"
                  color={colors.iconColor}
                  size={40}
                />
              </Link>
            </div>

            {/* Right Nav Links */}
            <div className="hidden lg:flex items-center space-x-10 flex-1 justify-end">
              {navLinks.slice(2).map((link) => (
                <div key={link.name} className="relative h-full flex items-center">
                  <NavLink link={link} />
                </div>
              ))}
            </div>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-6 ml-10">
              {/* Cart with live badge */}
              <button
                onClick={handleCartClick}
                className="relative flex gap-2 items-center hover:opacity-70 transition-opacity duration-200"
              >
                <CartIcon size={24} color={colors.iconColor} />
                <p className="font-medium" style={{ color: colors.text }}>
                  Cart
                </p>
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-xs bg-[#D4A574] text-white font-medium">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>

              {/* Account / Profile */}
              <div className="flex items-center space-x-3">
                {isAuthenticated ? (
                  <Link
                    to="/account"
                    className="flex gap-2 items-center hover:opacity-70 transition-opacity duration-200"
                  >
                    <div className="relative">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium"
                        style={{ backgroundColor: colors.activeColor }}
                      >
                        {getUserInitials()}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium mr-1" style={{ color: colors.text }}>
                        Hi, {user.firstName}
                      </span>
                    </div>
                  </Link>
                ) : (
                  <button
                    onClick={handleAccountClick}
                    className="flex gap-2 items-center hover:opacity-70 transition-opacity duration-200"
                  >
                    <AccountIcon size={24} color={colors.iconColor} />
                    <span className="font-medium" style={{ color: colors.text }}>
                      Account
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Mobile Actions */}
            <div className="flex items-center space-x-4 lg:hidden">
              <button
                onClick={handleCartClick}
                className="relative p-2 hover:opacity-70 transition-opacity duration-200"
              >
                <CartIcon size={22} color={colors.iconColor} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[10px] bg-[#D4A574] text-white font-medium">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:opacity-70 transition-opacity duration-200"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMenuOpen ? (
                  <CloseIcon className="w-6 h-6" color={colors.iconColor} />
                ) : (
                  <MenuIcon size={24} color={colors.iconColor} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
              onClick={() => setIsMenuOpen(false)}
            />

            <div
              ref={mobileMenuRef}
              className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-xl transform transition-transform duration-300 ease-in-out"
            >
              <div className="h-full flex flex-col overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <LogoIcon color="#1C2A46" size={32} />
                  </div>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-150"
                  >
                    <CloseIcon className="w-5 h-5 text-[#1C2A46]" />
                  </button>
                </div>

                <div className="flex-1 p-4">
                  <div className="space-y-1 mb-8">
                    {navLinks.map((link) => (
                      <div key={link.name} className="rounded-lg overflow-hidden">
                        <NavLink link={link} isMobile={true} />
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-100 pt-6">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-2">
                      Account
                    </h3>

                    {isAuthenticated ? (
                      <>
                        <Link
                          to="/account"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center space-x-3 px-6 py-4 bg-gray-50 rounded-lg mb-4 hover:bg-gray-100 transition-colors duration-150"
                        >
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                            style={{ backgroundColor: colors.activeColor }}
                          >
                            {getUserInitials()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-sm text-gray-600 truncate">
                              {user.email}
                            </p>
                          </div>
                        </Link>

                        <div className="space-y-1">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-2 mt-4">
                            Profile
                          </h4>
                          {profileMenuItems.map((item) => (
                            <Link
                              key={item.name}
                              to={item.path}
                              onClick={() => setIsMenuOpen(false)}
                              className="block py-3 px-6 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-150"
                            >
                              {item.name}
                            </Link>
                          ))}

                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-2 mt-4">
                            My Orders
                          </h4>
                          {orderMenuItems.map((item) => (
                            <Link
                              key={item.name}
                              to={item.path}
                              onClick={() => setIsMenuOpen(false)}
                              className="flex items-center justify-between py-3 px-6 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-150"
                            >
                              <span>{item.name}</span>
                              {item.badge && (
                                <span className="bg-[#D4A574] text-white text-xs px-2 py-1 rounded-full min-w-6 h-6 flex items-center justify-center">
                                  {item.badge}
                                </span>
                              )}
                            </Link>
                          ))}

                          <Link
                            to="/account/support"
                            onClick={() => setIsMenuOpen(false)}
                            className="block py-3 px-6 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-150"
                          >
                            Customer Support
                          </Link>

                          <button
                            onClick={() => {
                              logout();
                              setIsMenuOpen(false);
                            }}
                            className="block w-full text-left py-3 px-6 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150 mt-4"
                          >
                            Logout
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          openModal('signin');
                          setIsMenuOpen(false);
                        }}
                        className="block w-full text-left py-4 px-6 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-150 font-medium"
                      >
                        Sign In / Register
                      </button>
                    )}
                  </div>

                  <div className="border-t border-gray-100 pt-6 mt-6">
                    <div className="px-6 py-4 text-center">
                      <p className="text-sm text-gray-600">
                        Need help?{' '}
                        <Link
                          to="/contact"
                          className="text-[#D4A574] hover:underline"
                        >
                          Contact us
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {showCartModal && (
        <ShoppingBagModal onClose={() => setShowCartModal(false)} />
      )}
    </>
  );
};

export default Navbar;